import React from 'react';
import { Inputs, PackageType, IndustryType, RevenueCadence, DEFAULT_RAMP_PCTS, salesPerNinetyDayRep, withCumulativeRampSales } from '../utils/roiCalculations';
import { commissionsModeled, managerView } from '../utils/commissions';
import { SUMMER_PEST, withIndustry } from '../utils/summerProgram';
import { SummerPestSalesInputs, withVeteranRate } from './SummerPestSalesInputs';
import { CommissionInputs } from './CommissionInputs';
interface AssumptionsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  inputs: Inputs;
  onChange: (newInputs: Inputs) => void;
}
export function AssumptionsSidebar({
  isOpen,
  onClose,
  inputs,
  onChange
}: AssumptionsSidebarProps) {
  const handleChange = (field: keyof Inputs, value: string | number) => {
    onChange({
      ...inputs,
      [field]: value
    });
  };
  // Handle numeric input changes - allow empty string, convert to number only when valid
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    // If empty, set to empty string to allow clearing
    if (value === '') {
      onChange({
        ...inputs,
        [field]: ''
      });
      return;
    }
    // Otherwise parse as float
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onChange({
        ...inputs,
        [field]: numValue
      });
    }
  };
  // Cumulative first-90-days sales is a view of the ramp curve: editing it
  // rescales the three monthly percentages below.
  const [rampTotalDraft, setRampTotalDraft] = React.useState<string | null>(null);
  const commitRampTotal = () => {
    if (rampTotalDraft === null) return;
    const parsed = parseFloat(rampTotalDraft);
    setRampTotalDraft(null);
    if (Number.isNaN(parsed)) return;
    onChange(withCumulativeRampSales(inputs, Math.max(0, parsed)));
  };
  // Summer Pest also switches on the summer program and a Month 4 launch.
  const handleIndustryChange = (newIndustry: IndustryType) => onChange(withIndustry(inputs, newIndustry));
  const isSummerPest = inputs.industry === SUMMER_PEST;
  return <>
      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={onClose} data-id="element-159" data-mp="9tbzqqlij7ec" />}

      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-160" data-mp="3vz7hzfnh28gb">
        <div className="h-full flex flex-col" data-id="element-161" data-mp="3m8w34y292eun">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-navy" data-id="element-162" data-mp="3m7kxesp8ux0w">
            <h2 className="text-lg font-bold text-white" data-id="element-163" data-mp="3i5g5pzicwagw">
              Business Assumptions
            </h2>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-id="element-164" data-mp="389mwaozxox3c">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-165" data-mp="2t6v8xrjmthkh">
                <line x1="18" y1="6" x2="6" y2="18" data-id="element-166" data-mp="383lzrcf8pkyh" />
                <line x1="6" y1="6" x2="18" y2="18" data-id="element-167" data-mp="38346monvjmus" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4" data-id="element-168" data-mp="2feyj5u5frjr9">
            <div className="space-y-4" data-id="element-169" data-mp="2ffhrqk974vsm">
              {/* Company Info */}
              <div className="space-y-3" data-id="element-170" data-mp="2kdjb76ajax4m">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-171" data-mp="2kcgjwx9auso6">
                  Company Info
                </h3>

                <div data-id="element-172" data-mp="1rqfctk5y5h1t">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-173" data-mp="1rp4a7no8mfca">
                    Company Name
                  </label>
                  <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-174" data-mp="28i6oqetmwe4" />
                </div>

                <div data-id="element-175" data-mp="75s34q0mo8bg">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-176" data-mp="kyq5wxfeh2p1">
                    Industry
                  </label>
                  <select value={inputs.industry} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-177" data-mp="gwmrbzxfphzj">
                    <option value="" data-id="element-178" data-mp="2wqdjquwddvug">Select</option>
                    <option data-id="element-179" data-mp="2rqheksbxrydb">Windows</option>
                    <option data-id="element-180" data-mp="2rrsm938i5q9p">Roofing</option>
                    <option data-id="element-181" data-mp="1775dcbb0clke">Solar (Sales)</option>
                    <option data-id="element-182" data-mp="179rhe0yowixv">Solar (Vertical)</option>
                    <option data-id="element-183" data-mp="1c5gm0ipkgq11">Solar (Community)</option>
                    <option data-id="element-184" data-mp="1c8cmadovzqsn">Pest</option>
                    <option data-id="element-185" data-mp="xgkfiq35rl2j">Summer Pest</option>
                    <option data-id="element-186" data-mp="xfrgycecs1vh">Fiber</option>
                    <option data-id="element-187" data-mp="12fx1z9q4aq4l">Deregulated Energy</option>
                    <option data-id="element-188" data-mp="12em05fg8fsg4">Hardscaping</option>
                    <option data-id="element-189" data-mp="ogntyxc2f2bp">Landscaping</option>
                    <option data-id="element-190" data-mp="oj0dhexh1kqv">Other</option>
                  </select>
                </div>

                <div data-id="element-191" data-mp="thbj1dgqh93a">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-192" data-mp="eirc7momh94m">
                    Partnership
                  </label>
                  <select value={inputs.pkg} onChange={e => handleChange('pkg', e.target.value as PackageType)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none font-semibold" data-id="element-193" data-mp="jivt3dtg05vd">
                    <option value="" data-id="element-194" data-mp="2ccbxtgvkdnbd">Select</option>
                    <option value="advantage" data-id="element-195" data-mp="27ek3kg7g5p40">D2D HIRE Advantage</option>
                  </select>
                </div>
              </div>

              {/* Ad Spend */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-196" data-mp="1yf6xlxij7io1">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-197" data-mp="1yh9tuzaa2817">
                  Ad Spend
                </h3>

                <div data-id="element-198" data-mp="2q5l59bzybyr9">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-199" data-mp="2q7eaqnytwcxo">
                    Ad Spend per Month
                  </label>
                  <div className="relative" data-id="element-200" data-mp="2hdrj9a1nlvua">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-201" data-mp="2hcq3wtdlun6r">
                      $
                    </span>
                    <input type="number" min="0" step="100" value={inputs.adSpend} onChange={e => handleNumericChange('adSpend', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-202" data-mp="394z0rydbxhjv" />
                  </div>
                </div>

                <div data-id="element-203" data-mp="19jpz66alcrad">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-204" data-mp="1elp1rdyibk69">
                    Ad Cost per Hire
                  </label>
                  <div className="relative" data-id="element-205" data-mp="1jifg9wuny7pk">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-206" data-mp="1ob8myq9ozq4a">
                      $
                    </span>
                    <input type="number" min="1" step="50" value={inputs.cph} onChange={e => handleNumericChange('cph', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-207" data-mp="1sbshrn6smhn9" />
                  </div>
                </div>
              </div>

              {/* Revenue & Margins */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-208" data-mp="1i6yri3qhy89o">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-209" data-mp="1i4l0q72uni7x">
                  Revenue & Margins
                </h3>

                <div data-id="element-210" data-mp="pit57cjsob7q">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-211" data-mp="pjb7apt51jnk">
                    Revenue Cadence
                  </label>
                  <select value={inputs.revenueCadence} onChange={e => handleChange('revenueCadence', e.target.value as RevenueCadence)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-212" data-mp="yiq19z8ikcct">
                    <option value="one-time" data-id="element-213" data-mp="1ek0yui9gpz4">One-Time Sale</option>
                    <option value="monthly" data-id="element-214" data-mp="3iutmwocwbino">Monthly Residual</option>
                    <option value="quarterly" data-id="element-215" data-mp="3iwmse0brvwu3">Quarterly Residual</option>
                  </select>
                </div>

                <div data-id="element-216" data-mp="3nrjxwx9ufo28">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-217" data-mp="fdjry8t8xgyg">
                    {inputs.revenueCadence === 'one-time' ? 'Average Ticket' : 'Initial Sale Ticket'}
                  </label>
                  <div className="relative" data-id="element-218" data-mp="5fnfndokofyr">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-219" data-mp="aegvvdv8ul2m">
                      $
                    </span>
                    <input type="number" min="0" step="500" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-220" data-mp="2q4tg2jyu3vi6" />
                  </div>
                </div>

                {inputs.revenueCadence !== 'one-time' && <div data-id="element-221" data-mp="2nbnugs27v7zr">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-222" data-mp="2nb4911dcy1gz">
                      {inputs.revenueCadence === 'monthly' ? 'Monthly Residual' : 'Quarterly Residual'}
                    </label>
                    <div className="relative" data-id="element-223" data-mp="2icaxvfljmmjz">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-224" data-mp="2id2soo7bgg97">
                        $
                      </span>
                      <input type="number" min="0" step="10" value={inputs.residualTicket || 0} onChange={e => handleNumericChange('residualTicket', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-225" data-mp="glpsgcgamwjh" />
                    </div>
                  </div>}

                {inputs.revenueCadence === 'monthly' && <div data-id="element-226" data-mp="2frd0a2mw78fs">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-227" data-mp="2fs4vhcckv45j">
                      Monthly Attrition
                    </label>
                    <div className="relative" data-id="element-228" data-mp="2ost8q4u5d0hd">
                      <input type="number" min="0" max="100" step="1" value={inputs.monthlyAttrition || 2} onChange={e => handleNumericChange('monthlyAttrition', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-229" data-mp="2otw16h74baze" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-230" data-mp="38ezp9nhvl23l">
                        %
                      </span>
                    </div>
                  </div>}

                {inputs.revenueCadence === 'quarterly' && <div data-id="element-231" data-mp="3n6rzjl2tbpyg">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-232" data-mp="3r7kb4kz5q6xw">
                      Quarterly Attrition
                    </label>
                    <div className="relative" data-id="element-233" data-mp="2u0q3dcc4z5">
                      <input type="number" min="0" max="100" step="1" value={inputs.quarterlyAttrition || 5} onChange={e => handleNumericChange('quarterlyAttrition', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-234" data-mp="503yc6avvmy2" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-235" data-mp="sv70ttgun3hl">
                        %
                      </span>
                    </div>
                  </div>}

                <CommissionInputs inputs={inputs} onChange={handleNumericChange} onSet={handleChange} variant="sidebar" data-id="element-236" data-mp="k2lvbf28atap" />

                <div className={managerView(inputs) ? 'hidden' : undefined} data-id="element-237" data-mp="1bt044wvdxffi">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-238" data-mp="wbiaohb263wl">
                    {commissionsModeled(inputs) ? 'Margin Before Commissions' : 'Gross Profit Margin'}
                  </label>
                  <div className="relative" data-id="element-239" data-mp="rdgrzxhjo6ln">
                    <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-240" data-mp="rei2o0uner2u" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-241" data-mp="1y25g3e24cdhb">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Sales Performance */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-242" data-mp="3nh7x42zacp59">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-243" data-mp="3ij6bpbee0dqm">
                  Per Rep Sales Performance
                </h3>

                <div data-id="element-244" data-mp="3s9r76pyrl25b">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-245" data-mp="3s97r7esocntx">
                    Monthly Sales After 90 Days
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesVetMonthly} onChange={e => {
                  const v = parseFloat(e.target.value);
                  if (isSummerPest && !Number.isNaN(v) && v > 0) {
                    onChange(withVeteranRate(inputs, v));
                    return;
                  }
                  handleNumericChange('salesVetMonthly', e.target.value);
                }} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-246" data-mp="34m9d2ns11rz6" />
                </div>

                {isSummerPest && <SummerPestSalesInputs inputs={inputs} onChange={onChange} variant="sidebar" data-id="element-247" data-mp="15u54bdczsjzu" />}

                {/* Ramp curve: each new rep's first three producing months as a
                    share of a veteran's monthly output. */}
                <div className={`rounded border border-gray-200 bg-gray-50 p-3 ${isSummerPest ? 'hidden' : ''}`} data-id="element-248" data-mp="i53q19hhaemn">
                  <div className="text-xs font-bold text-navy uppercase tracking-wide mb-1" data-id="element-249" data-mp="rv5fa4lki335">
                    Ramp-Up Pace
                  </div>
                  <p className="text-xs text-gray-600 mb-3" data-id="element-250" data-mp="3qkdnu5ec4xom">
                    How much a new rep sells in each of their first three
                    months, compared to a fully ramped rep.
                  </p>
                  <div className="grid grid-cols-3 gap-2" data-id="element-251" data-mp="2cm9o9ktp7t47">
                    {([['rampMonth1Pct', 'Mo 1', DEFAULT_RAMP_PCTS[0]], ['rampMonth2Pct', 'Mo 2', DEFAULT_RAMP_PCTS[1]], ['rampMonth3Pct', 'Mo 3', DEFAULT_RAMP_PCTS[2]]] as const).map(([field, label, fallback]) => <div key={field} data-id="element-252" data-mp="1oz9w8vwb1rdw">
                        <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-253" data-mp="1oy88pry3rcfa">
                          {label}
                        </label>
                        <div className="relative" data-id="element-254" data-mp="1fylhfyz37mzy">
                          <input type="number" min="0" max="200" step="5" value={inputs[field] ?? fallback} onChange={e => handleNumericChange(field, e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-255" data-mp="1fzwms386l2t6" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-256" data-mp="2pv04s9tr7vos">
                            %
                          </span>
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-3" data-id="element-257" data-mp="2fyx3f66q812w">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-258" data-mp="226gw3t6slgw6">
                      Total Sales in First 90 Days
                    </label>
                    <input type="number" min="0" step={inputs.industry === SUMMER_PEST ? 5 : 0.5} value={rampTotalDraft ?? Math.round(salesPerNinetyDayRep(inputs) * 10) / 10} onChange={e => setRampTotalDraft(e.target.value)} onBlur={commitRampTotal} onKeyDown={e => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }} title="Editing this rescales the three monthly percentages above" className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-259" data-mp="1s7hwt17x2xlu" />
                  </div>
                </div>

                <div data-id="element-260" data-mp="268ylxx7uatkk">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-261" data-mp="26bku9z2k1d3u">
                    Pullthrough / Realization
                  </label>
                  <div className="relative" data-id="element-262" data-mp="2g8pf9vknz2j2">
                    <input type="number" min="0" max="100" step="1" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-263" data-mp="2g59xgkzlvfdy" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-264" data-mp="2so9sos09nj8l">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Retention Model */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-265" data-mp="2dtdfs1hii7p6">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-266" data-mp="292n8ujtftwsd">
                  Retention Model
                </h3>

                <div data-id="element-267" data-mp="1lbhrg07umwfy">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-268" data-mp="1lazko9nk8zts">
                    Hired to Launched
                  </label>
                  <div className="relative" data-id="element-269" data-mp="176r05ent5wpw">
                    <input type="number" min="0" max="100" step="1" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-270" data-mp="175zdwug3pbca" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-271" data-mp="1uwcvts4z1tt5">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-272" data-mp="1kx43t4to2p9f">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-273" data-mp="1l0ryw8erfjq9">
                    Launched to Day 30
                  </label>
                  <div className="relative" data-id="element-274" data-mp="9js6zdwbr6wr">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-275" data-mp="9hfisizz40b9" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-276" data-mp="1sizon8s6pnlz">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-277" data-mp="1jib9ubuyvj81">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-278" data-mp="1jjma41p7e8ue">
                    Day 30 to Day 90
                  </label>
                  <div className="relative" data-id="element-279" data-mp="3dvswlwnjsc20">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-280" data-mp="3duritkf5dbgl" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-281" data-mp="3v3k41zcy83r0">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-282" data-mp="32gzdh14arvoi">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-283" data-mp="32gg26395k5jg">
                    Day 90 to 1 Year
                  </label>
                  <div className="relative" data-id="element-284" data-mp="3cex0xmec1og2">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-285" data-mp="3cfoxazfr7q7e" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-286" data-mp="z8pbh3dk1mru">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}